import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../api/ApiRoutes";

export function getAuthedInstance() {
  const token = JSON.parse(localStorage.getItem("token") ?? "null");
  return createPrivateAdminPanelInstance(token?.AccessToken);
}

export type CollectionGameTag = {
  tag_id: string;
  name: string;
  name_translations?: Record<string, string>;
  color?: string;
  is_seed?: boolean;
  /** Jackpot (and other special tags) may carry extra meta, e.g. amount. */
  meta?: { amount?: number | string } | null;
};

export type CollectionGame = {
  game_id: string;
  provider: string;
  name: string;
  image?: string;
  external_image_url?: string;
  custom_image?: string;
  is_active: boolean;
  is_pinned?: boolean;
  display_order?: number | null;
  aggregator?: boolean;
  freespins_available?: boolean;
  tags?: CollectionGameTag[];
};

export type CollectionGamesResult = {
  items: CollectionGame[];
  total: number;
  meta?: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
};

export const DEFAULT_PAGE_SIZE = 200;

export async function fetchCollectionGames(
  code: string,
  view: "all" | "popular" = "all",
  search?: string,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
): Promise<CollectionGamesResult> {
  const instance = getAuthedInstance();

  const params: Record<string, unknown> = { view, page, page_size: pageSize };
  if (search) params.search = search;

  const url = API_ROUTES.GET_COLLECTION_GAMES.replace("{code}", encodeURIComponent(code));
  const { data: body } = await instance.get(url, { params });

  if (body?.success === false) {
    throw new Error(body?.message || "Failed to fetch collection games");
  }

  const data = body?.data;
  const items = Array.isArray(data?.items) ? data.items : [];
  const total = data?.total ?? data?.meta?.total ?? items.length;

  return { items, total, meta: data?.meta };
}

export function useCollectionGames(
  code: string | null,
  view: "all" | "popular" = "all",
  search?: string,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: ["casinoConfiguration", "collections", code, "games", view, search || "", page, pageSize],
    queryFn: () => fetchCollectionGames(code!, view, search, page, pageSize),
    enabled: Boolean(code),
  });
}

export type CollectionGameGroup = {
  provider: string;
  game_count?: number;
  is_pinned?: boolean;
  display_order?: number | null;
};

export async function fetchCollectionGameGroups(
  code: string,
): Promise<CollectionGameGroup[]> {
  const instance = getAuthedInstance();
  const url = API_ROUTES.GET_COLLECTION_GAME_GROUPS.replace(
    "{code}",
    encodeURIComponent(code),
  );
  const { data: body } = await instance.get(url);
  if (body?.success === false) {
    throw new Error(body?.message || "Failed to fetch game groups");
  }
  const data = body?.data;
  return Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
}

export function useCollectionGameGroups(code: string | null) {
  return useQuery({
    queryKey: ["casinoConfiguration", "collections", code, "games", "groups"],
    queryFn: () => fetchCollectionGameGroups(code!),
    enabled: Boolean(code),
  });
}

export async function fetchCollectionProviderGames(
  code: string,
  provider: string,
  search?: string,
): Promise<CollectionGamesResult> {
  const instance = getAuthedInstance();
  const url = API_ROUTES.GET_COLLECTION_PROVIDER_GAMES
    .replace("{code}", encodeURIComponent(code))
    .replace("{provider}", encodeURIComponent(provider));
  const params: Record<string, unknown> = { page: 1, page_size: DEFAULT_PAGE_SIZE };
  if (search) params.search = search;
  const { data: body } = await instance.get(url, { params });
  if (body?.success === false) {
    throw new Error(body?.message || "Failed to fetch provider games");
  }
  const data = body?.data;
  const items = Array.isArray(data?.items) ? data.items : [];
  const total = data?.total ?? data?.meta?.total ?? items.length;
  return { items, total, meta: data?.meta };
}

export function useCollectionProviderGames(
  code: string | null,
  provider: string | null,
  search?: string,
) {
  return useQuery({
    queryKey: [
      "casinoConfiguration", "collections", code, "games", "groups", provider, search || "",
    ],
    queryFn: () => fetchCollectionProviderGames(code!, provider!, search),
    enabled: Boolean(code) && Boolean(provider),
  });
}

export function useRemoveCollectionGame(collectionCode: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      gameId,
      provider,
    }: {
      gameId: string;
      provider: string;
    }) => {
      const instance = getAuthedInstance();
      const url = API_ROUTES.DELETE_COLLECTION_GAME
        .replace("{code}", encodeURIComponent(collectionCode))
        .replace("{provider}", encodeURIComponent(provider))
        .replace("{game_id}", encodeURIComponent(gameId));
      const { data } = await instance.delete(url);
      if (data?.success === false) {
        throw new Error(data?.message || "Failed to remove game");
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          "casinoConfiguration",
          "collections",
          collectionCode,
          "games",
        ],
      });
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections"],
      });
    },
  });
}

export function useMoveCollectionGame(collectionCode: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      view: string;
      provider: string;
      game_id: string;
      target_position: number;
      groupedByProvider?: boolean;
    }) => {
      const instance = getAuthedInstance();
      let url: string;
      if (params.groupedByProvider && params.game_id) {
        url = API_ROUTES.MOVE_COLLECTION_GAME_PROVIDER
          .replace("{code}", encodeURIComponent(collectionCode))
          .replace("{provider}", encodeURIComponent(params.provider));
      } else if (params.groupedByProvider) {
        url = API_ROUTES.MOVE_COLLECTION_PROVIDER_GROUP.replace(
          "{code}",
          encodeURIComponent(collectionCode),
        );
      } else {
        url = API_ROUTES.MOVE_COLLECTION_GAME.replace(
          "{code}",
          encodeURIComponent(collectionCode),
        );
      }
      const { groupedByProvider: _, game_id, view, ...rest } = params;
      const payload: Record<string, unknown> = { ...rest };
      if (!params.groupedByProvider) {
        payload.view = view;
        if (game_id) payload.game_id = game_id;
      } else if (game_id) {
        payload.game_id = game_id;
      }
      const { data } = await instance.post(url, payload);
      if (data?.success === false) {
        throw new Error(data?.message || "Failed to move game");
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections", collectionCode, "games"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections", "config"],
      });
    },
  });
}

/**
 * Casino-admin move endpoints take a SIGNED, band-relative target_position:
 * -N = Nth slot of the pinned band (pins the row), +N = Nth slot of the
 * unpinned band (unpins it). Lists render pinned rows first, so convert a
 * flat drop index (row lands at `dropIdx` after the drag) into that shape.
 * `pinnedCount` and `dropIdx` must describe the same list the user is
 * looking at; when that list is a filtered or paginated subset of the
 * backend order (search active, page > 1), the resulting slot is only as
 * accurate as that subset.
 */
export function toBandRelativePosition({
  dropIdx,
  movedIsPinned,
  pinnedCount,
}: {
  dropIdx: number;
  movedIsPinned: boolean;
  pinnedCount: number;
}): number {
  if (dropIdx < pinnedCount) return -(dropIdx + 1);
  const pinnedAfter = pinnedCount - (movedIsPinned ? 1 : 0);
  return dropIdx - pinnedAfter + 1;
}

/** `target_position` for an "unpin" toggle: top of the unpinned band. */
export const UNPIN_TO_TOP = 1;

type OrderableRow = {
  is_pinned?: boolean;
  display_order?: number | null;
};

/**
 * Visible list number: one continuous 1, 2, 3… across pinned + unpinned.
 * Do not render API `display_order` — that value restarts per band
 * (pinned 1–7 then unpinned 1, 2, 5… as on all_games page 1).
 */
export function toVisualListPosition(listIndex: number): number {
  return listIndex + 1;
}

/**
 * Move `target_position` from the drop-target row's `display_order`.
 *
 * GET /collections/{code}/games (`view=all`) returns:
 * - pinned: reverse-signed (`-1` is the last pinned row)
 * - unpinned: `+1…+N` (Hot Coins Fortune 10 = `1`, Leonardo's Secret = `5`)
 *
 * The move API wants `-1` = top of the pinned band, `+N` = Nth unpinned slot.
 */
export function toMoveTargetPosition({
  target,
  dropIdx,
  movedIsPinned,
  items,
}: {
  target: OrderableRow;
  dropIdx: number;
  movedIsPinned: boolean;
  items: OrderableRow[];
}): number {
  const pinned = items.filter((item) => item.is_pinned);
  const pinnedCount = pinned.length;

  if (target.is_pinned) {
    const pinnedRank = pinned.indexOf(target);
    if (pinnedRank >= 0) return -(pinnedRank + 1);
    const order = target.display_order;
    if (order != null && order < 0 && pinnedCount > 0) {
      return -(pinnedCount + order + 1);
    }
    return -(Math.min(dropIdx, Math.max(pinnedCount - 1, 0)) + 1);
  }

  const order = target.display_order;
  if (order != null && Number.isFinite(order) && order > 0) {
    return order;
  }
  return toBandRelativePosition({ dropIdx, movedIsPinned, pinnedCount });
}

export async function fetchCollectionConfig() {
  const instance = getAuthedInstance();
  const { data: body } = await instance.get(API_ROUTES.GET_COLLECTION_CONFIG);
  return body?.data ?? body ?? {};
}

export function useCollectionConfig() {
  return useQuery({
    queryKey: ["casinoConfiguration", "collections", "config"],
    queryFn: () => fetchCollectionConfig(),
  });
}

export function useTogglePopularGame(collectionCode: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      provider: string;
      game_id: string;
      is_popular: boolean;
    }) => {
      const instance = getAuthedInstance();
      const url = API_ROUTES.COLLECTION_GAME_POPULAR
        .replace("{code}", encodeURIComponent(collectionCode))
        .replace("{provider}", encodeURIComponent(params.provider))
        .replace("{game_id}", encodeURIComponent(params.game_id));

      let data;
      if (params.is_popular) {
        ({ data } = await instance.post(url));
      } else {
        ({ data } = await instance.delete(url));
      }
      if (data?.success === false) {
        throw new Error(data?.message || "Failed to update popular status");
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections", collectionCode, "games"],
      });
    },
  });
}

export type AvailableGame = {
  provider: string;
  game_id: string;
  name: string;
  is_active: boolean;
};

export async function fetchAvailableGames(
  excludeCollectionCode: string,
  page = 1,
  pageSize = 50,
  search?: string,
): Promise<{ items: AvailableGame[]; total: number }> {
  const instance = getAuthedInstance();
  const params: Record<string, unknown> = {
    page,
    page_size: pageSize,
    exclude_collection_code: excludeCollectionCode,
  };
  if (search) params.search = search;

  const { data: body } = await instance.get(API_ROUTES.GET_ADMIN_GAMES, { params });

  if (body?.success === false) {
    throw new Error(body?.message || "Failed to fetch available games");
  }

  const data = body?.data;
  const items = Array.isArray(data?.items) ? data.items : [];
  const total = data?.total ?? data?.meta?.total ?? items.length;
  return { items, total };
}

export function useAvailableGames(
  excludeCollectionCode: string | null,
  search?: string,
  page = 1,
  pageSize = 50,
) {
  return useQuery({
    queryKey: [
      "casinoConfiguration",
      "availableGames",
      excludeCollectionCode,
      search || "",
      page,
      pageSize,
    ],
    queryFn: () =>
      fetchAvailableGames(excludeCollectionCode!, page, pageSize, search),
    enabled: Boolean(excludeCollectionCode),
  });
}

export function useAddCollectionGames(collectionCode: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (games: { provider: string; game_id: string }[]) => {
      const instance = getAuthedInstance();
      const url = API_ROUTES.POST_COLLECTION_GAMES.replace(
        "{code}",
        encodeURIComponent(collectionCode),
      );
      const { data } = await instance.post(url, { games });
      if (data?.success === false) {
        throw new Error(data?.message || "Failed to add games");
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections", collectionCode, "games"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "availableGames"],
      });
    },
  });
}
